import type { ActionTree, GetterTree, Module, MutationTree } from "vuex";
import type { RootState } from "../state";
import type { MasterState, ShowSnackbarPayload } from "../modules/master-state";
import { Enums } from "../../../common/helper/enums";

const state: MasterState = {
  showSnackbar: false,
  snackbarMessage: "",
  snackbarColor: "",
  snackbarTimeout: 2000,
  snackbarIcon: "",

  appIconLink: "",
  appFaviconLink: "",
  resetTheme: 0,
  loginBrandingLoading: false,
  showContactPreference: true,
  showQuickConnect: false,
  serviceProviderId: -1,

  formEntityLoaded: false,

  unsavedChangesDialog: false,
  unsavedChangesConfirm: function () {},
  unsavedChangesCancel: function () {},
  formEmailKey: "",
  selectedProjectId: "",
  ticketTypeSettings: null,
};

const getters: GetterTree<MasterState, RootState> = {
  showSnackbar(state): boolean {
    return state.showSnackbar;
  },

  snackbarMessage(state): string {
    return state.snackbarMessage;
  },

  snackbarColor(state): string {
    return state.snackbarColor;
  },

  snackbarIcon(state): string {
    return state.snackbarIcon;
  },

  snackbarTimeout(state): number {
    return state.snackbarTimeout;
  },

  appIconLink(state): string {
    return state.appIconLink;
  },

  appFaviconLink(state): string {
    return state.appFaviconLink;
  },

  resetTheme(state): number {
    return state.resetTheme;
  },

  loginBrandingLoading(state): boolean {
    return state.loginBrandingLoading;
  },

  showContactPreference(state): boolean {
    return state.showContactPreference;
  },

  showQuickConnect(state): boolean {
    return state.showQuickConnect;
  },

  formEntityLoaded(state): boolean {
    return state.formEntityLoaded;
  },

  getServiceProviderId(state): number {
    return state.serviceProviderId;
  },

  unsavedChangesModal(state): boolean {
    return state.unsavedChangesDialog;
  },

  clickUnsavedChangesConfirm(state): () => void {
    return state.unsavedChangesConfirm;
  },

  clickUnsavedChangesCancel(state): () => void {
    return state.unsavedChangesCancel;
  },

  getFormEmailKey(state): string {
    return state.formEmailKey;
  },

  getSelectedProjectId(state): string {
    return state.selectedProjectId;
  },

  getTicketTypeSettings(state): unknown {
    return state.ticketTypeSettings;
  },
};

const actions: ActionTree<MasterState, RootState> = {
  ShowSnackbar({ commit }, payload: ShowSnackbarPayload) {
    commit("SET_SHOW_SNACKBAR", false);
    commit("SET_SNACKBAR_COLOR", payload.isSuccess ? "success" : "error");
    commit("SET_SNACKBAR_MESSAGE", payload.message);
    commit("SET_SNACKBAR_TIMEOUT", payload.Timeout);
    commit("SET_SNACKBAR_ICON", payload.Icon);
    commit("SET_SHOW_SNACKBAR", true);
  },

  ShowUnsavedChangesDialog({ commit, getters }, payload): void {
    commit("SET_UNSAVEDCANCELEVENT", payload.eventCancel);
    commit("SET_UNSAVEDCONFIRMEVENT", payload.event);
    commit("SET_UNSAVEDCHANGESDIALOG", true);
  },

  ConfirmUnsavedChangesBox({ commit, getters }): void {
    getters.clickUnsavedChangesConfirm && getters.clickUnsavedChangesConfirm();
    commit("SET_UNSAVEDCHANGESDIALOG", false);
  },

  AbortUnsavedChangesBox({ commit, getters }): void {
    getters.clickUnsavedChangesCancel && getters.clickUnsavedChangesCancel();
    commit("SET_UNSAVEDCHANGESDIALOG", false);
  },

  SetFormEmailKey({ commit }, payload): void {
    commit("SET_FORM_EMAIL_KEY", payload.formEmailKey);
  },
};

const mutations: MutationTree<MasterState> = {
  SET_UNSAVEDCONFIRMEVENT(state, value: () => void) {
    state.unsavedChangesConfirm = value;
  },
  SET_UNSAVEDCANCELEVENT(state, value: () => void) {
    state.unsavedChangesCancel = value;
  },
  SET_UNSAVEDCHANGESDIALOG(state, value: boolean) {
    state.unsavedChangesDialog = value;
  },

  SET_SHOW_SNACKBAR(state, value: boolean) {
    state.showSnackbar = value;
  },

  SET_SNACKBAR_MESSAGE(state, value: string) {
    state.snackbarMessage = value;
  },

  SET_SNACKBAR_TIMEOUT(state, value: number) {
    state.snackbarTimeout = value;
  },

  SET_SNACKBAR_COLOR(state, value: string) {
    state.snackbarColor = value;
  },

  SET_SNACKBAR_ICON(state, value: Enums.SnackbarIcon) {
    if (value === Enums.SnackbarIcon.InProgress) {
      state.snackbarIcon = "fa-regular fa-spinner";
    } else if (value === Enums.SnackbarIcon.Success) {
      state.snackbarIcon = "fa-regular fa-check";
    } else if (value === Enums.SnackbarIcon.Failure) {
      state.snackbarIcon = "fa-regular fa-exclamation";
    } else {
      state.snackbarIcon = "";
    }
  },

  SET_APP_ICON_LINK(state, value: string) {
    state.appIconLink = value;
  },

  SET_APP_FAVICON_LINK(state, value: string) {
    state.appFaviconLink = value;
  },

  SET_RESET_THEME(state) {
    state.resetTheme += 1;
  },

  SET_LOGIN_BRANDING_LOADING(state, value: boolean) {
    state.loginBrandingLoading = value;
  },

  SET_SHOW_CONTACT_PREFERENCE(state, value: boolean) {
    state.showContactPreference = value;
  },

  SET_SHOW_QUICK_CONNECT(state, value: boolean) {
    state.showQuickConnect = value;
  },

  SET_FORM_ENTITY_LOADED(state, value: boolean) {
    state.formEntityLoaded = value;
  },

  SET_SERVICE_PROVIDER_ID(state, value: number) {
    state.serviceProviderId = value;
  },

  SET_FORM_EMAIL_KEY(state, value: string) {
    state.formEmailKey = value;
  },

  SET_SELECTED_PROJECT_ID(state, value: string) {
    state.selectedProjectId = value;
  },

  SET_TICKET_TYPE_SETTINGS(state, value: unknown) {
    state.ticketTypeSettings = value;
  },
};

const namespaced: boolean = true;

export const MasterModule: Module<MasterState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
};
